import { fromJS, List } from 'immutable'

import { createImmutableSelector } from 'utils/reselect'
import { getViewConfigModuleName } from './news/view_config'

// DATA
export const getSavedSearchesData = state => state.savedSearches.get('data')
export const getAllSavedSearches = state => getSavedSearchesData(state).get('sorted')
export const getSavedSearchById = (state, id) => getSavedSearchesData(state).getIn(['entities', id])
export const getSavedSearchesDataSorted = state => getSavedSearchesData(state).get('sorted')

export const getSavedSearchesByModule = (state, moduleName) => (
  getSavedSearchesData(state).get('sorted').filter(s => s.get('moduleName') === moduleName)
)

export const getSavedSearches = createImmutableSelector(
  getViewConfigModuleName,
  getSavedSearchesDataSorted,
  (moduleName, savedSearches) => savedSearches.filter(s => s.get('moduleName') === moduleName)
)

// UI
export const getSavedSearchesUi = state => state.savedSearches.get('ui')
export const getSavedSearchesLoading = state => getSavedSearchesUi(state).get('loading')
export const getInitialSearchLoaded = state => getSavedSearchesUi(state).get('initialSearchLoaded')
export const getSavedSearchResolving = state => getSavedSearchesUi(state).get('resolving')
export const getSavedSearchesTab = state => getSavedSearchesUi(state).get('tab')
export const getSavedSearchMenuCollapsedGroupIds = state => getSavedSearchesUi(state).get('menuCollapsedGroupIds')
export const getSavedSearchMenuGrouped = state => getSavedSearchesUi(state).get('menuGrouped')

// DIALOGS
export const getSavedSearchesDialogs = state => state.savedSearches.get('dialogs')

// DATA
export const getSelectedSavedSearch = state => getSavedSearchesDialogs(state).get('data').get('selectedSavedSearch')
export const getExecutedSavedSearch = state => getSavedSearchesDialogs(state).get('data').get('executedSavedSearch')

// UI
export const getSavedSearchesDialogsUi = state => getSavedSearchesDialogs(state).get('ui')
export const getShowRemoveSavedSearchDialog = state => getSavedSearchesDialogsUi(state).get('removeActive')
export const getShowAffectedDashboardsDialog = state => getSavedSearchesDialogsUi(state).get('affectedDashboardsActive')
export const getShowUpdateSavedSearchDialog = state => getSavedSearchesDialogsUi(state).get('updateActive')
export const getShowEditSavedSearchDialog = state => getSavedSearchesDialogsUi(state).get('editActive')
export const getSavedSearchesUploadedPhoto = state => getSavedSearchesDialogsUi(state).get('uploadedPhoto')
export const getShowSavedSearchAlertHistoryDialog = state => getSavedSearchesDialogsUi(state).get('alertHistoryActive')
export const getAffectedDashboards = state => getSavedSearchesDialogsUi(state).get('affectedDashboards')

// TIMELINES
export const getSavedSearchesTimelines = state => state.savedSearches.get('timelines')

// FEEDS
export const getSavedSearchesFeedsData = state => state.savedSearches.getIn(['feeds', 'data'])
export const getSavedSearchesFeedsDataSorted = state => state.savedSearches.getIn(['feeds', 'data', 'sorted'])

export const getSavedSearchesFeeds = createImmutableSelector(
  getViewConfigModuleName,
  getSavedSearchesFeedsDataSorted,
  getSavedSearchesData,
  (moduleName, feeds, savedSearches) => feeds
    .map(feed => feed.set('savedSearch', savedSearches.getIn(['entities', feed.get('savedSearchId')])))
    .filter(feed => feed.getIn(['savedSearch', 'moduleName']) === moduleName)
)

export const getSavedSearchesFeedsTotalCount = state => getSavedSearchesFeedsData(state).get('entities').size
export const getSavedSearchesFeedsUi = state => state.savedSearches.getIn(['feeds', 'ui'])
export const getSavedSearchesFeedsUiDialogFeed = state => getSavedSearchesFeedsUi(state).get('dialogFeed')
export const getSavedSearchesFeedsUiSaving = state => getSavedSearchesFeedsUi(state).get('saving')
export const getSavedSearchesFeedsUiDeleting = state => getSavedSearchesFeedsUi(state).get('deleting')
export const getSavedSearchesFeedsUiCreating = state => getSavedSearchesFeedsUi(state).get('creating')

// GROUPS
export const getSavedSearchGroupsData = state => state.savedSearches.getIn(['groups', 'data'])
export const getSavedSearchGroups = createImmutableSelector(
  getViewConfigModuleName,
  getSavedSearchGroupsData,
  (moduleName, groups) => groups.filter(group => group.get('moduleName') === moduleName)
)

const savedSearchesById = savedSearches => savedSearches
  .groupBy(savedSearch => String(savedSearch.get('id')))
  .map(matches => matches.first())

export const getSavedSearchMenuGroups = createImmutableSelector(
  getSavedSearches,
  getSavedSearchGroups,
  (savedSearches, groups) => {
    const searchesById = savedSearchesById(savedSearches)

    return groups
      .sortBy(group => (group.get('name') || '').toLowerCase())
      .map(group => {
        const memberships = group.get('grootSavedSearchesSavedSearchGroups') || List()
        const items = memberships
          .map(membership => searchesById.get(String(membership.get('savedSearchId'))))
          .filter(Boolean)
          .sortBy(savedSearch => (savedSearch.get('name') || '').toLowerCase())

        return fromJS({
          id: group.get('id'),
          name: group.get('name')
        }).set('items', items)
      })
      .filter(group => !group.get('items').isEmpty())
  }
)

export const getUngroupedSavedSearches = createImmutableSelector(
  getSavedSearches,
  getSavedSearchGroups,
  (savedSearches, groups) => {
    const groupedIds = groups
      .flatMap(group => (
        (group.get('grootSavedSearchesSavedSearchGroups') || List())
          .map(membership => String(membership.get('savedSearchId')))
      ))
      .toSet()

    return savedSearches.filter(savedSearch => !groupedIds.has(String(savedSearch.get('id'))))
  }
)

export const getSavedSearchGroupsUi = state => state.savedSearches.getIn(['groups', 'ui'])
export const getSelectedSavedSearchGroup = state => getSavedSearchGroupsUi(state).get('selectedGroup')
export const getSavedSearchGroupSaving = state => getSavedSearchGroupsUi(state).get('saving')
export const getSavedSearchGroupDeleting = state => getSavedSearchGroupsUi(state).get('deleting')
export const getShowSavedSearchGroupDialog = state => getSavedSearchGroupsUi(state).get('showGroupDialog')
export const getShowDeleteSavedSearchGroupDialog = state => getSavedSearchGroupsUi(state).get('showDeleteGroupDialog')

export const getAssignSavedSearchesToGroup = state => state.savedSearches.get('assignSavedSearchesToGroup')
export const getShowAssignSavedSearchesToGroupDialog = state => getAssignSavedSearchesToGroup(state).get('show')
export const getAssignSavedSearchesToGroupSelectedGroupId = state => getAssignSavedSearchesToGroup(state).get('selectedGroupId')
export const getSavingSavedSearchGroupIds = state => getAssignSavedSearchesToGroup(state).get('savingSavedSearchIds')

export const getSavedSearchGroupForAssign = createImmutableSelector(
  getSavedSearchGroups,
  getAssignSavedSearchesToGroupSelectedGroupId,
  (groups, groupId) => groups.find(group => group.get('id') === groupId) || null
)
